import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}


// Duplicate announcement items once so the CSS ticker loops without a gap.
const enhanceOffFindsAnnouncementLoop = (root = document) => {
  root.querySelectorAll('.announcement-bar__slides').forEach((track) => {
    if (track.dataset.offFindsLoop === 'true') return;

    const slides = [...track.querySelectorAll(':scope > .announcement-bar__slide')];
    if (!slides.length) return;

    slides.forEach((slide) => {
      const clone = document.createElement('div');
      clone.className = slide.className;
      clone.innerHTML = slide.innerHTML;
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.offFindsClone = 'true';
      track.append(clone);
    });

    track.dataset.offFindsLoop = 'true';
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enhanceOffFindsAnnouncementLoop(), { once: true });
} else {
  requestAnimationFrame(() => enhanceOffFindsAnnouncementLoop());
}

document.addEventListener('shopify:section:load', (event) => {
  enhanceOffFindsAnnouncementLoop(event.target);
});


// The marquee owns motion; stop the original slide timer from interrupting it.
const stabilizeOffFindsTicker = (root = document) => {
  root.querySelectorAll('announcement-bar-component').forEach((bar) => {
    bar.removeAttribute('autoplay');
    if (typeof bar.suspend === 'function') bar.suspend();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => stabilizeOffFindsTicker(), { once: true });
} else {
  requestAnimationFrame(() => stabilizeOffFindsTicker());
}

document.addEventListener('shopify:section:load', (event) => stabilizeOffFindsTicker(event.target));


// Build two identical static halves so the -50% animation boundary is seamless.
const normalizeOffFindsTicker = (root = document) => {
  root.querySelectorAll('.announcement-bar__slides').forEach((track) => {
    const source = [...track.querySelectorAll('[data-off-finds-clone="true"]')];
    if (!source.length || track.dataset.offFindsNormalized === 'true') return;

    const makeItem = (item, isClone) => {
      const copy = document.createElement('div');
      copy.className = item.className;
      copy.innerHTML = item.innerHTML;
      copy.setAttribute('aria-hidden', isClone ? 'true' : 'false');
      if (isClone) copy.dataset.offFindsClone = 'true';
      return copy;
    };

    const firstHalf = source.map((item) => makeItem(item, false));
    const secondHalf = source.map((item) => makeItem(item, true));
    track.replaceChildren(...firstHalf, ...secondHalf);
    track.dataset.offFindsNormalized = 'true';
  });
};

const initializeOffFindsTicker = (root = document) => {
  enhanceOffFindsAnnouncementLoop(root);
  stabilizeOffFindsTicker(root);
  normalizeOffFindsTicker(root);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeOffFindsTicker(), { once: true });
} else {
  requestAnimationFrame(() => initializeOffFindsTicker());
}

document.addEventListener('shopify:section:load', (event) => initializeOffFindsTicker(event.target));
